import { random } from "@chatbotgang/etude/string/random";
import { define } from "@chatbotgang/etude/util/define";
import {
  FIREBASE_SHARED_RESOURCE_CDN_BASE_URL,
  FIREBASE_SHARED_RESOURCE_STORAGE_BUCKET,
} from "@polifonia/env";
import type {
  FirebaseStorage,
  StorageObserver,
  UploadTaskSnapshot,
} from "firebase/storage";
import { getStorage, ref, uploadBytesResumable } from "firebase/storage";
import { nanoid } from "nanoid";
import PQueue from "p-queue";
import type { PathFunction } from "path-to-regexp";
import { compile } from "path-to-regexp";
import { extname } from "pathe";

import { sharedResourceFirebaseApp } from "@/lib/firebase/sharedResourceFirebaseApp";

const features = [
  "userAvatar",
  "brandLogo",
  "launcherWidget",
  "webStreamHero",
] as const;
type Feature = (typeof features)[number];

const featureConfigs = define<
  Record<
    Feature,
    {
      toPath: PathFunction<any>;
      storage: FirebaseStorage;
    }
  >
>()({
  userAvatar: {
    toPath: compile<{
      dialogueId: string;
      hash: string;
      fileName: string;
    }>("clients/:dialogueId/avatars/:hash/:fileName"),
    storage: getStorage(
      sharedResourceFirebaseApp,
      `gs://${FIREBASE_SHARED_RESOURCE_STORAGE_BUCKET}`,
    ),
  },
  brandLogo: {
    toPath: compile<{
      dialogueId: string;
      hash: string;
      fileName: string;
    }>("clients/:dialogueId/logos/:hash/:fileName"),
    storage: getStorage(
      sharedResourceFirebaseApp,
      `gs://${FIREBASE_SHARED_RESOURCE_STORAGE_BUCKET}`,
    ),
  },
  launcherWidget: {
    toPath: compile<{
      dialogueId: string;
      hash: string;
      fileName: string;
    }>("clients/:dialogueId/launcher/:hash/:fileName"),
    storage: getStorage(
      sharedResourceFirebaseApp,
      `gs://${FIREBASE_SHARED_RESOURCE_STORAGE_BUCKET}`,
    ),
  },
  webStreamHero: {
    toPath: compile<{
      dialogueId: string;
      hash: string;
      fileName: string;
    }>("clients/:dialogueId/webstream-hero/:hash/:fileName"),
    storage: getStorage(
      sharedResourceFirebaseApp,
      `gs://${FIREBASE_SHARED_RESOURCE_STORAGE_BUCKET}`,
    ),
  },
});

type FeatureConfig<F extends Feature> = (typeof featureConfigs)[F];

const fileNameKey = "fileName";
const hashKey = "hash";
type FileNameKey = typeof fileNameKey;
type HashKey = typeof hashKey;

type BaseParams<F extends Feature> = {
  feature: F;
  pathParams: Omit<
    NonNullable<Parameters<FeatureConfig<F>["toPath"]>[0]>,
    FileNameKey | HashKey
  >;
  file: File;
  randomizeFileName?: boolean;
  signal?: AbortSignal;
  onNext?: (uploadTaskSnapshot: UploadTaskSnapshot) => void;
};

function randomizeFilename(fileName: string): string {
  const ext = extname(fileName);
  const name = random() + ext;
  return name;
}

async function asyncUploadBytesResumable(
  {
    signal,
    ...storageObserver
  }: Pick<StorageObserver<UploadTaskSnapshot>, "next"> & {
    signal?: AbortSignal;
  },
  ...args: Parameters<typeof uploadBytesResumable>
) {
  const uploadTask = uploadBytesResumable(...args);
  uploadTask.on("state_changed", {
    next(uploadTaskSnapshot) {
      storageObserver.next?.(uploadTaskSnapshot);
    },
  });
  if (signal?.aborted) {
    uploadTask.cancel();
  } else {
    signal?.addEventListener("abort", () => {
      uploadTask.cancel();
    });
  }
  return uploadTask;
}

async function raw<F extends Feature>({
  feature,
  file,
  randomizeFileName: randomizeFileNameOption = true,
  signal,
  pathParams,
  onNext,
}: BaseParams<F>) {
  const featureConfig: FeatureConfig<F> = featureConfigs[feature];
  const storage = featureConfig.storage;
  const toPath: FeatureConfig<F>["toPath"] = featureConfig.toPath;
  const hash = nanoid();
  const filePath = toPath({
    ...pathParams,
    [hashKey]: hash,
    [fileNameKey]: randomizeFileNameOption
      ? randomizeFilename(file.name)
      : file.name,
  } as any);
  const storageReference = ref(storage, filePath);
  const uploadTaskSnapshot = await asyncUploadBytesResumable(
    {
      ...(onNext && { next: onNext }),
      ...(signal && { signal }),
    },
    storageReference,
    file,
    {
      contentType: file.type,
    },
  );
  signal?.throwIfAborted();
  const downloadUrl = `${FIREBASE_SHARED_RESOURCE_CDN_BASE_URL}/wccs/${filePath}`;
  signal?.throwIfAborted();
  return {
    uploadTaskSnapshot,
    downloadUrl,
  };
}

const CONCURRENCY = 2;

const pQueue = new PQueue({ concurrency: CONCURRENCY });

const internal: typeof raw = (...args) =>
  pQueue.add(() => raw(...args), { throwOnTimeout: true });

const uploadToFirebaseStorage: <F extends Feature>(
  params: BaseParams<F>,
) => ReturnType<typeof internal> = internal;

export { uploadToFirebaseStorage };
export type { BaseParams, Feature };
